import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Input,
  InputGroup,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Alert,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import { format } from "date-fns";
import ApiService from "../../services/ApiService";

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    fullName: "",
    role: "user",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const itemsPerPage = 10;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ApiService.getUsers(
        currentPage,
        itemsPerPage,
        searchTerm
      );
      setUsers(data.users || []);
      setTotalPages(Math.ceil((data.total || 0) / itemsPerPage));
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      username: "",
      email: "",
      fullName: "",
      role: "user",
      password: "",
      confirmPassword: "",
    });
    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    if (user.role === "admin") {
      setError("Admin accounts cannot be edited for security reasons");
      setTimeout(() => setError(""), 3000);
      return;
    }

    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      fullName: user.fullName,
      role: user.role,
      password: "",
      confirmPassword: "",
    });
    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!editingUser && formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!editingUser && formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    try {
      const submitData = { ...formData };
      if (editingUser && !submitData.password) {
        delete submitData.password;
        delete submitData.confirmPassword;
      }

      if (editingUser) {
        await ApiService.updateUser(editingUser.id, submitData);
        setSuccess("User updated successfully!");
      } else {
        await ApiService.createUser(submitData);
        setSuccess("User created successfully!");
      }

      fetchUsers();
      setTimeout(() => {
        setModalOpen(false);
        setSuccess("");
      }, 1500);
    } catch (err) {
      setError(err.message || "Operation failed");
    }
  };

  const deleteUser = async (userId, username) => {
    // Find the user to check if it's an admin
    const user = users.find((u) => u.id === userId);

    if (user && user.role === "admin") {
      setError("Admin accounts cannot be deleted for security reasons");
      setTimeout(() => setError(""), 3000);
      return;
    }

    if (window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      try {
        await ApiService.deleteUser(userId);
        setSuccess("User deleted successfully!");
        fetchUsers();
        setTimeout(() => setSuccess(""), 3000);
      } catch (err) {
        setError(err.message || "Failed to delete user");
        setTimeout(() => setError(""), 3000);
      }
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    // Find the user to check if it's an admin
    const user = users.find((u) => u.id === userId);

    if (user && user.role === "admin") {
      setError("Admin account status cannot be changed for security reasons");
      setTimeout(() => setError(""), 3000);
      return;
    }

    try {
      await ApiService.toggleUserStatus(userId, !currentStatus);
      fetchUsers();
    } catch (err) {
      console.error("Failed to toggle user status:", err);
    }
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      admin: "danger",
      user: "primary",
    };
    return <Badge color={roleColors[role] || "secondary"}>{role}</Badge>;
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return dateString;
    }
  };

  return (
    <div className="app-main__inner">
      <div className="app-page-title">
        <div className="page-title-wrapper">
          <div className="page-title-heading">
            <div className="page-title-icon">
              <i className="pe-7s-users icon-gradient bg-mean-fruit"></i>
            </div>
            <div>
              User Management
              <div className="page-title-subheading">
                Create and manage system users
              </div>
            </div>
          </div>
          <div className="page-title-actions">
            <Button
              color="primary"
              onClick={openCreateModal}
              style={{ color: "white" }}
            >
              <i className="fa fa-plus me-1"></i>
              Add User
            </Button>
          </div>
        </div>
      </div>

      {success && (
        <Alert color="success" className="mb-3">
          {success}
        </Alert>
      )}
      {error && (
        <Alert color="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Row>
        <Col md="12">
          <Card className="main-card mb-3">
            <CardHeader>
              <i className="header-icon pe-7s-id icon-gradient bg-plum-plate"></i>
              System Users
              <div className="btn-actions-pane-right">
                <InputGroup size="sm" style={{ width: "300px" }}>
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </InputGroup>
              </div>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center">
                  <div className="loader-container-inner">
                    <div className="text-center">Loading users...</div>
                  </div>
                </div>
              ) : (
                <>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length > 0 ? (
                        users.map((user) => (
                          <tr key={user.id}>
                            <td>{user.username}</td>
                            <td>{user.email}</td>
                            <td>{getRoleBadge(user.role)}</td>
                            <td>
                              <Badge
                                color={user.active ? "success" : "secondary"}
                              >
                                {user.active ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                            <td>{formatDate(user.createdAt)}</td>
                            <td>
                              <Button
                                color="warning"
                                size="sm"
                                onClick={() => openEditModal(user)}
                                className="me-1"
                                disabled={user.role === "admin"}
                                id={`edit-btn-${user.id}`}
                              >
                                Edit
                              </Button>
                              {user.role === "admin" && (
                                <UncontrolledTooltip
                                  placement="top"
                                  target={`edit-btn-${user.id}`}
                                >
                                  Admin accounts cannot be edited
                                </UncontrolledTooltip>
                              )}
                              <Button
                                color={user.active ? "secondary" : "success"}
                                size="sm"
                                onClick={() =>
                                  toggleUserStatus(user.id, user.active)
                                }
                                className="me-1"
                                disabled={user.role === "admin"}
                                id={`status-btn-${user.id}`}
                              >
                                {user.active ? "Deactivate" : "Activate"}
                              </Button>
                              {user.role === "admin" && (
                                <UncontrolledTooltip
                                  placement="top"
                                  target={`status-btn-${user.id}`}
                                >
                                  Admin accounts cannot be deactivated
                                </UncontrolledTooltip>
                              )}
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() =>
                                  deleteUser(user.id, user.username)
                                }
                                disabled={user.role === "admin"}
                                id={`delete-btn-${user.id}`}
                              >
                                Delete
                              </Button>
                              {user.role === "admin" && (
                                <UncontrolledTooltip
                                  placement="top"
                                  target={`delete-btn-${user.id}`}
                                >
                                  Admin accounts cannot be deleted
                                </UncontrolledTooltip>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No users found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center">
                      <Pagination>
                        <PaginationItem disabled={currentPage === 1}>
                          <PaginationLink
                            previous
                            onClick={() => setCurrentPage(currentPage - 1)}
                          />
                        </PaginationItem>
                        {[...Array(totalPages)].map((_, index) => (
                          <PaginationItem
                            key={index}
                            active={currentPage === index + 1}
                          >
                            <PaginationLink
                              onClick={() => setCurrentPage(index + 1)}
                            >
                              {index + 1}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem disabled={currentPage === totalPages}>
                          <PaginationLink
                            next
                            onClick={() => setCurrentPage(currentPage + 1)}
                          />
                        </PaginationItem>
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Create/Edit User Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={() => setModalOpen(false)}>
            {editingUser ? "Edit User" : "Create New User"}
          </ModalHeader>
          <ModalBody>
            {error && <Alert color="danger">{error}</Alert>}
            {success && <Alert color="success">{success}</Alert>}

            <FormGroup>
              <Label for="username">Username *</Label>
              <Input
                type="text"
                name="username"
                id="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                disabled={!!editingUser}
              />
            </FormGroup>

            <FormGroup>
              <Label for="fullName">Full Name *</Label>
              <Input
                type="text"
                name="fullName"
                id="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label for="email">Email *</Label>
              <Input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label for="role">Role *</Label>
              <Input
                type="select"
                name="role"
                id="role"
                value={formData.role}
                onChange={handleInputChange}
                required
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Input>
            </FormGroup>

            <FormGroup>
              <Label for="password">
                Password {!editingUser && "*"}
                {editingUser && (
                  <small className="text-muted">
                    (leave blank to keep current password)
                  </small>
                )}
              </Label>
              <Input
                type="password"
                name="password"
                id="password"
                value={formData.password}
                onChange={handleInputChange}
                required={!editingUser}
                minLength="6"
              />
            </FormGroup>

            {!editingUser && (
              <FormGroup>
                <Label for="confirmPassword">Confirm Password *</Label>
                <Input
                  type="password"
                  name="confirmPassword"
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  minLength="6"
                />
              </FormGroup>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="primary" type="submit">
              {editingUser ? "Update User" : "Create User"}
            </Button>
            <Button color="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUsers;
